import { useCallback, useEffect, useState } from 'react'
import { useOwlbertWebAuth } from '../auth/OwlbertWebAuthContext'
import { buildHostedWorkspaceNavModules, type HostedWorkspaceNavModule } from '../utils/hostedWorkspaceNavModules'

/** Dispatched from settings after a successful save so the shell can refetch enabled modules. */
export const HOSTED_WORKSPACE_MODULES_CHANGED = 'owlbert:hosted-workspace-modules-changed'

export function useHostedWorkspaceNavModules(): {
  items: HostedWorkspaceNavModule[]
  loading: boolean
  reload: () => void
} {
  const auth = useOwlbertWebAuth()
  const [items, setItems] = useState<HostedWorkspaceNavModule[]>([])
  const [loading, setLoading] = useState(false)

  const reload = useCallback(() => {
    const client = auth.bundle?.client
    const workspaceId = auth.workspaceId
    if (!client || !workspaceId || !auth.sessionReady) {
      setItems([])
      setLoading(false)
      return
    }
    setLoading(true)
    void client.owlbertHosted.workspaceModules.get
      .query({ workspaceId })
      .then((res) => {
        setItems(buildHostedWorkspaceNavModules(res.enabled_modules))
      })
      .catch(() => {
        setItems([])
      })
      .finally(() => {
        setLoading(false)
      })
  }, [auth.bundle?.client, auth.sessionReady, auth.workspaceId])

  useEffect(() => {
    reload()
  }, [reload])

  useEffect(() => {
    const onChanged = () => reload()
    window.addEventListener(HOSTED_WORKSPACE_MODULES_CHANGED, onChanged)
    return () => window.removeEventListener(HOSTED_WORKSPACE_MODULES_CHANGED, onChanged)
  }, [reload])

  return { items, loading, reload }
}
